import { useEffect, useState } from "react";
import api from "@api";
import { useParams, useNavigate, NavLink, useLocation } from "react-router-dom";
import "./Season.css";
import Breadcrumbs from "../../../../../components/Breadcrumbs.jsx";
import Divisions from "../../Tournament/Auth/Divisions.jsx";
import { useAuth } from "../../../../../context/AuthContext";

function isTeamInBracket(bracketData, teamId) {
  if (!bracketData) return false;

  for (const round of bracketData.rounds) {
    for (const match of round) {
      if (!match) continue;
      if (match.teamA?.slt_id === teamId) return true;
      if (match.teamB?.slt_id === teamId) return true;
    }
  }

  return false;
}

function removeTeamFromBracketSlots(bracketData, teamId) {
  const updated = JSON.parse(JSON.stringify(bracketData));

  for (const round of updated.rounds) {
    for (const match of round) {
      if (!match) continue;

      if (match.teamA?.slt_id === teamId) {
        match.teamA = {
          name: match.teamA._slotLabel,
          slt_id: null,
          _slotLabel: match.teamA._slotLabel,
        };
      }

      if (match.teamB?.slt_id === teamId) {
        match.teamB = {
          name: match.teamB._slotLabel,
          slt_id: null,
          _slotLabel: match.teamB._slotLabel,
        };
      }
    }
  }

  return updated;
}

function Season() {
  const { id: season_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(
    () => new URLSearchParams(location.search).get("tab") || "leagues"
  );

  const [leagues, setLeagues] = useState([]);
  const [leagueForm, setLeagueForm] = useState({
    name: "",
    copyFromLeagueId: "",
    league_type: "team",
  });

  const [updateForm, setUpdateForm] = useState({
    id: null,
    name: "",
    league_type: "team",
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedLeagues, setSelectedLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  const [teams, setTeams] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [selectedDivisionId, setSelectedDivisionId] = useState(null);
  const [draggedTeam, setDraggedTeam] = useState(null);
  const [draggedLeague, setDraggedLeague] = useState(null);

  const { organization } = useAuth();
  const [features, setFeatures] = useState([]);

  useEffect(() => {
    const fetchFeatures = async () => {
      if (!organization?.id) return;

      try {
        const res = await api.get(
          `/api/organization-features/${organization.id}`,
          { withCredentials: true }
        );

        setFeatures(res.data.features || []);
      } catch (err) {
        setFeatures([]);
      }
    };

    fetchFeatures();
  }, [organization?.id]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [leaguesRes, divisionsRes] = await Promise.all([
          api.get(`/api/seasonsv2/${season_id}/leagues-with-teams-public`),
          api.get(`/api/seasonsv2/${season_id}/divisions`),
        ]);

        const leaguesData = leaguesRes.data.leagues || [];

        const allTeams = leaguesData.flatMap((league) =>
          league.teams?.map((team) => ({
            ...team,
            leagueName: league.name,
          })) || []
        );

        const divisionsData = (divisionsRes.data || [])
          .map((division) => ({
            ...division,
            teams: [...(division.teams || [])].sort(
              (a, b) => (a.position ?? 999) - (b.position ?? 999)
            ),
          }))
          .sort((a, b) =>
            a.name.localeCompare(b.name, undefined, {
              numeric: true,
              sensitivity: "base",
            })
          );

        const assignedSltIds = new Set(
          divisionsData.flatMap((division) =>
            division.teams.map((team) => team.slt_id)
          )
        );

        const updatedTeams = allTeams.map((team) => ({
          ...team,
          isAssigned: assignedSltIds.has(team.slt_id),
        }));

        setLeagues(leaguesData);
        setTeams(updatedTeams);
        setDivisions(divisionsData);
      } catch (err) {
        console.error("Error loading season data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [season_id]);

  const unassignedTeams = teams.filter((team) => !team.isAssigned);

  const handleUnassignTeamFromDivision = async (divisionId, slt_id) => {
    try {
      await api.delete(`/api/divisions/${divisionId}/teams/${slt_id}`);

      setDivisions((prev) =>
        prev.map((division) => {
          if (division.id !== divisionId) return division;

          const updatedTeams = division.teams
            .filter((team) => team.slt_id !== slt_id)
            .map((team, index) => ({
              ...team,
              position: index,
            }));

          api.put(
            `/api/divisions/${divisionId}/teams/order`,
            {
              orderedTeams: updatedTeams.map((team) => ({
                slt_id: team.slt_id,
                position: team.position,
              })),
            },
            { withCredentials: true }
          );

          return {
            ...division,
            teams: updatedTeams,
          };
        })
      );

      setTeams((prev) =>
        prev.map((team) =>
          team.slt_id === slt_id ? { ...team, isAssigned: false } : team
        )
      );
    } catch (err) {
      if (err.response?.status === 409) {
        alert(err.response.data.message);
        return;
      }

      console.error("Error unassigning team from division:", err);
    }
  };

  const handleDropTeamIntoDivision = async (divisionId, team) => {
    try {
      const division = divisions.find((item) => item.id === divisionId);
      const alreadyInDivision = division?.teams?.some(
        (divisionTeam) => divisionTeam.slt_id === team.slt_id
      );

      if (alreadyInDivision) return;

      const newPosition = division.teams?.length ?? 0;

      await api.post(`/api/divisions/${divisionId}/teams`, {
        seasons_leagues_teams_id: team.slt_id,
        position: newPosition,
      });

      setDivisions((prev) =>
        prev.map((divisionItem) =>
          divisionItem.id === divisionId
            ? {
              ...divisionItem,
              teams: [
                ...(divisionItem.teams || []),
                {
                  ...team,
                  leagueName: team.leagueName || "—",
                  wins: team.wins ?? 0,
                  losses: team.losses ?? 0,
                  slt_id: team.slt_id,
                  id: team.id,
                  position: divisionItem.teams?.length ?? 0,
                },
              ],
            }
            : divisionItem
        )
      );

      setTeams((prev) =>
        prev.map((item) =>
          item.slt_id === team.slt_id ? { ...item, isAssigned: true } : item
        )
      );
    } catch (err) {
      console.error("Error assigning team:", err);
    }
  };

  const handleCreateLeague = async (e) => {
    e.preventDefault();

    try {
      const res = await api.post(
        `/api/seasonsv2/${season_id}/leagues`,
        {
          ...leagueForm,
          league_type: showLeagueType ? leagueForm.league_type : "team",
          season_id: Number(season_id),
          copyFromSeasonId: leagueForm.copyFromSeasonId || null,
        },
        { withCredentials: true }
      );

      setLeagues((prev) => [...prev, res.data.league]);

      setLeagueForm({
        name: "",
        copyFromSeasonId: "",
        league_type: "team",
      });

      setIsModalOpen(false);
      setMessage(`${res.data.league.name} created`);
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Create error:", err);
    }
  };

  const handleUpdateLeague = async (e) => {
    e.preventDefault();

    try {
      const res = await api.put(
        `/api/seasonsv2/${season_id}/leagues/${updateForm.id}`,
        {
          id: updateForm.id,
          name: updateForm.name,
        },
        { withCredentials: true }
      );

      setLeagues((prev) =>
        prev.map((league) =>
          league.id === updateForm.id ? res.data.league : league
        )
      );

      setIsUpdateModalOpen(false);
      setMessage(`${res.data.league.name} updated`);
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Update error:", err);
    }
  };

  const handleDragStart = (league) => {
    setDraggedLeague(league);
  };

  const handleDrop = async (targetLeague) => {
    if (!draggedLeague || draggedLeague.id === targetLeague.id) return;

    const currentIndex = leagues.findIndex(
      (league) => league.id === draggedLeague.id
    );
    const targetIndex = leagues.findIndex(
      (league) => league.id === targetLeague.id
    );

    const updated = [...leagues];
    const [moved] = updated.splice(currentIndex, 1);
    updated.splice(targetIndex, 0, moved);

    const orderedLeagues = updated.map((league, index) => ({
      id: league.id,
      position: index,
    }));

    setLeagues(updated);

    await api.put(`/api/seasonsv2/${season_id}/leagues/order`, {
      orderedLeagues,
    });
  };

  const handleDeleteLeagues = async () => {
    if (!selectedLeagues.length) {
      return alert("Select at least one league.");
    }

    if (!window.confirm("Delete selected leagues?")) return;

    try {
      await Promise.all(
        selectedLeagues.map((id) =>
          api.delete(`/api/seasonsv2/${season_id}/leagues/${id}`, {
            withCredentials: true,
          })
        )
      );

      setLeagues((prev) =>
        prev.filter((league) => !selectedLeagues.includes(league.id))
      );

      setSelectedLeagues([]);
      setMessage("Leagues deleted");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const openUpdateModal = (league) => {
    setUpdateForm({
      id: league.id,
      name: league.name,
      league_type: league.league_type || "team",
      hasLeagueData: league.hasLeagueData || false,
    });

    setIsUpdateModalOpen(true);
  };

  const handleCheckboxChange = (id) => {
    setSelectedLeagues((prev) =>
      prev.includes(id)
        ? prev.filter((leagueId) => leagueId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedLeagues.length === leagues.length) {
      setSelectedLeagues([]);
    } else {
      setSelectedLeagues(leagues.map((league) => league.id));
    }
  };

  const onReorderTeams = async (divisionId, newOrder) => {
    setDivisions((prev) =>
      prev.map((division) =>
        division.id === divisionId
          ? {
            ...division,
            teams: newOrder,
          }
          : division
      )
    );

    await api.put(`/api/divisions/${divisionId}/teams/order`, {
      orderedTeams: newOrder.map((team, index) => ({
        slt_id: team.slt_id ?? team.id,
        position: index,
      })),
    });
  };

  const handleUnassignTeamsFromDeletedDivision = (teamsToUnassign) => {
    setTeams((prev) =>
      prev.map((team) =>
        teamsToUnassign.some(
          (unassigned) => unassigned.slt_id === team.slt_id
        )
          ? { ...team, isAssigned: false }
          : team
      )
    );
  };

  const hasFeature = (key) =>
    features.some((feature) => feature.feature_key === key && feature.enabled);

  const sportName =
    organization?.sports?.[0]?.type ||
    organization?.sport?.type ||
    organization?.sport ||
    "";

  const showLeagueType = ["tennis", "pickleball"].includes(
    sportName.toLowerCase()
  );

  const getLeagueTypeLabel = (leagueType) => {
    if (leagueType === "individual") return "Individual";
    if (leagueType === "doubles_pair") return "Doubles Pair";
    return "Team";
  };

  const getLeagueEntryCount = (league) => {
    if (
      league.league_type === "individual" ||
      league.league_type === "doubles_pair"
    ) {
      return league.playersCount || 0;
    }

    return league.teamsCount || 0;
  };

  return (
    <div className="seasonDetailsAuthPage">
      <Breadcrumbs />

      {message && (
        <div className="seasonDetailsAuthPage-message">{message}</div>
      )}

      <div className="seasonDetailsAuthPage-tabs">
        <button
          type="button"
          className={
            activeTab === "leagues"
              ? "seasonDetailsAuthPage-tabBtn seasonDetailsAuthPage-tabActive"
              : "seasonDetailsAuthPage-tabBtn"
          }
          onClick={() => {
            setActiveTab("leagues");
            navigate(`/dashboard/seasons/${season_id}?tab=leagues`, {
              replace: true,
            });
          }}
        >
          Leagues
        </button>

        {hasFeature("brackets") && (
          <button
            type="button"
            className={
              activeTab === "tournament"
                ? "seasonDetailsAuthPage-tabBtn seasonDetailsAuthPage-tabActive"
                : "seasonDetailsAuthPage-tabBtn"
            }
            onClick={() => {
              setActiveTab("tournament");
              navigate(`/dashboard/seasons/${season_id}?tab=tournament`, {
                replace: true,
              });
            }}
          >
            Tournament
          </button>
        )}
      </div>

      {activeTab === "leagues" && (
        <div className="seasonDetailsAuthPage-card">
          <div className="seasonDetailsAuthPage-header">
            <div>
              <h2 className="seasonDetailsAuthPage-title">Leagues</h2>

              <p className="seasonDetailsAuthPage-subtitle">
                Create leagues, reorder them, and open league management.
              </p>
            </div>

            <div className="seasonDetailsAuthPage-actions">
              <button
                type="button"
                className="seasonDetailsAuthPage-primaryBtn"
                onClick={() => setIsModalOpen(true)}
              >
                + Add League
              </button>

              <button
                type="button"
                className="seasonDetailsAuthPage-dangerBtn"
                onClick={handleDeleteLeagues}
                disabled={selectedLeagues.length === 0}
                title={
                  selectedLeagues.length === 0
                    ? "Select leagues first"
                    : "Delete selected leagues"
                }
              >
                Delete Selected
                {selectedLeagues.length > 0 && ` (${selectedLeagues.length})`}
              </button>
            </div>
          </div>

          <div className="seasonDetailsAuthPage-tableWrap">
            <table className="seasonDetailsAuthPage-table">
              <thead>
                <tr>
                  <th className="seasonDetailsAuthPage-checkboxCol">
                    <input
                      type="checkbox"
                      checked={
                        leagues.length > 0 &&
                        selectedLeagues.length === leagues.length
                      }
                      onChange={handleSelectAll}
                    />
                  </th>

                  <th>#</th>
                  <th>League Name</th>

                  {showLeagueType && <th>Type</th>}

                  <th>Entries</th>
                  <th className="seasonDetailsAuthPage-actionCol"></th>
                </tr>
              </thead>

              <tbody>
                {loading ? null : leagues.length === 0 ? (
                  <tr>
                    <td
                      colSpan={showLeagueType ? "6" : "5"}
                      className="seasonDetailsAuthPage-emptyCell"
                    >
                      No leagues available
                    </td>
                  </tr>
                ) : (
                  leagues.map((league, index) => (
                    <tr
                      key={league.id}
                      draggable
                      onDragStart={() => handleDragStart(league)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => handleDrop(league)}
                    >
                      <td className="seasonDetailsAuthPage-checkboxCol">
                        <input
                          type="checkbox"
                          checked={selectedLeagues.includes(league.id)}
                          onChange={() => handleCheckboxChange(league.id)}
                        />
                      </td>

                      <td>{index + 1}</td>

                      <td>
                        <NavLink
                          className="seasonDetailsAuthPage-nameLink"
                          to={`/dashboard/seasons/${season_id}/leagues/${league.id}`}
                        >
                          {league.name}
                        </NavLink>
                      </td>

                      {showLeagueType && (
                        <td>
                          <span className="seasonDetailsAuthPage-typePill">
                            {getLeagueTypeLabel(league.league_type)}
                          </span>
                        </td>
                      )}

                      <td>{getLeagueEntryCount(league)}</td>

                      <td className="seasonDetailsAuthPage-actionCol">
                        <button
                          type="button"
                          className="seasonDetailsAuthPage-pencilBtn"
                          onClick={() => openUpdateModal(league)}
                        >
                          ✏️
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {hasFeature("brackets") && activeTab === "tournament" && (
        <div className="seasonDetailsAuthPage-tournamentGrid">
          <div className="seasonDetailsAuthPage-tournamentPanel">
            <Divisions
              seasonId={season_id}
              divisions={
                selectedDivisionId
                  ? divisions.filter(
                    (division) => division.id === Number(selectedDivisionId)
                  )
                  : divisions
              }
              divisionFilterDropdown={
                divisions.length > 1 && (
                  <div className="seasonDetailsAuthPage-divisionFilter">
                    <label>Select Division</label>

                    <select
                      value={selectedDivisionId || ""}
                      onChange={(e) =>
                        setSelectedDivisionId(e.target.value || null)
                      }
                    >
                      <option value="">All Divisions</option>

                      {divisions.map((division) => (
                        <option key={division.id} value={division.id}>
                          {division.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )
              }
              setDivisions={setDivisions}
              onDropTeam={handleDropTeamIntoDivision}
              onRemoveTeam={handleUnassignTeamFromDivision}
              onReorderTeams={onReorderTeams}
              onUnassignTeamsFromDeletedDivision={
                handleUnassignTeamsFromDeletedDivision
              }
            />
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="seasonDetailsAuthPage-modalOverlay">
          <div className="seasonDetailsAuthPage-modal">
            <div className="seasonDetailsAuthPage-modalHeader">
              <div>
                <h3>Create League</h3>
                <p>Add a new league to this season.</p>
              </div>

              <button
                type="button"
                className="seasonDetailsAuthPage-closeBtn"
                onClick={() => setIsModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <form
              className="seasonDetailsAuthPage-form"
              onSubmit={handleCreateLeague}
            >
              <label>
                League Name
                <input
                  type="text"
                  placeholder="League name"
                  value={leagueForm.name}
                  onChange={(e) =>
                    setLeagueForm({
                      ...leagueForm,
                      name: e.target.value,
                    })
                  }
                  required
                />
              </label>

              {showLeagueType && (
                <label>
                  League Type
                  <select
                    value={leagueForm.league_type}
                    onChange={(e) =>
                      setLeagueForm({
                        ...leagueForm,
                        league_type: e.target.value,
                      })
                    }
                  >
                    <option value="team">Team</option>
                    <option value="individual">Individual</option>
                    <option value="doubles_pair">Doubles Pair</option>
                  </select>
                </label>
              )}

              <div className="seasonDetailsAuthPage-modalActions">
                <button
                  type="submit"
                  className="seasonDetailsAuthPage-primaryBtn"
                >
                  Create League
                </button>

                <button
                  type="button"
                  className="seasonDetailsAuthPage-secondaryBtn"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isUpdateModalOpen && (
        <div className="seasonDetailsAuthPage-modalOverlay">
          <div className="seasonDetailsAuthPage-modal">
            <div className="seasonDetailsAuthPage-modalHeader">
              <div>
                <h3>Update League</h3>
                <p>Edit the league name.</p>
              </div>

              <button
                type="button"
                className="seasonDetailsAuthPage-closeBtn"
                onClick={() => setIsUpdateModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <form
              className="seasonDetailsAuthPage-form"
              onSubmit={handleUpdateLeague}
            >
              <label>
                League Name
                <input
                  type="text"
                  value={updateForm.name}
                  onChange={(e) =>
                    setUpdateForm({
                      ...updateForm,
                      name: e.target.value,
                    })
                  }
                  required
                />
              </label>

              <div className="seasonDetailsAuthPage-modalActions">
                <button
                  type="submit"
                  className="seasonDetailsAuthPage-primaryBtn"
                >
                  Update League
                </button>

                <button
                  type="button"
                  className="seasonDetailsAuthPage-secondaryBtn"
                  onClick={() => setIsUpdateModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Season;